import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type ProductData } from "~/domains/product/types/ProductData";
import { canUseCommentsForProduct } from "~/utils/productUtils";
import { useState } from "react";
import { Product } from "~/domains/product/Product";
import classes from "./B2CProductListItem.module.less";
import { IconButton } from "~/common/mui";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { CommentButton } from "~/common/components/CommentButton/CommentButton";
import { useDebouncedState } from "~/common/hooks/useDebouncedValue";

interface LineItemProps {
  data: LineItem;
  product: ProductData;
  onDelete: () => void;
  onChange: (changes: Partial<LineItem>) => void;
}

export const B2CProductListItem = ({ data, product, onChange, onDelete }: LineItemProps) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useDebouncedState({
    value: data.quantity,
    onChange: (quantity) => onChange({ quantity }),
    delay: 500,
  });
  const [comment, setComment] = useDebouncedState({
    value: data.comment,
    onChange: (comment) => onChange({ comment }),
    delay: 500,
  });
  const { toggleCommentOpen, isCommentOpen, canSeeComment } = useLineItemCommentLogic({
    comment: comment,
    product,
  });
  const packSize = data.packSize ?? 1;
  const [commentRef, toggleComment] = useShowAndScrollInput(toggleCommentOpen);
  return (
    <Product.Context value={product}>
      <div className={classes.root}>
        <div className={classes.description}>
          <Product.PrimaryImageWithLogos />
          <div className={classes.designation}>
            <Product.PreferredIndicator className={"icon-margin"} />
            <Product.Designation />
          </div>
          <Product.MMDTag />
          <Product.ShortDescription clickable={false} />
        </div>
        <div className={classes.subItem}>
          <div className={classes.actions}>
            <IconButton color={"primary"} className={classes.iconButton} onClick={onDelete}>
              <Icon type={IconType.Bin} className={classes.icon} />
            </IconButton>
            {canSeeComment && (
              <CommentButton onClick={toggleComment} open={isCommentOpen} fontSize={"small"} className={classes.icon} />
            )}
          </div>
          <QuantityInput
            className={classes.quantity}
            onValueChange={setQuantity}
            value={quantity}
            step={packSize}
            min={packSize}
          />
          <div className={classes.comment} hidden={!isCommentOpen}>
            <CommentInput
              value={data.comment ?? ""}
              onChange={(comment) => onChange({ comment })}
              placeholder={t("page.cart.b2c.item.comment.placeholder")}
              ref={commentRef}
            />
          </div>
        </div>
      </div>
    </Product.Context>
  );
};
const useLineItemCommentLogic = ({
  comment,
  product,
  initiallyOpen = true,
}: Pick<LineItem, "comment"> & {
  comment?: string;
  initiallyOpen?: boolean;
  product: ProductData;
}) => {
  const canSeeComment = canUseCommentsForProduct(product.marketType);
  const [isCommentOpen, setIsCommentOpen] = useState(() => canSeeComment && initiallyOpen);
  const toggleCommentOpen = () => setIsCommentOpen((prev) => !prev);
  return { toggleCommentOpen, isCommentOpen, canSeeComment };
};
